/* Which tickets have a new reply this person has not read (S10 part B).
 *
 * The server decides - GET /api/tickets/unread answers per row, by the caller's
 * relationship to the ticket, and leaves internal notes out (api/src/shared/unread.js).
 * This file only remembers the last answer for the screens: the bell's count and the
 * "New reply" mark on My Tickets and the Queue.
 *
 *   null      not known (walkthrough build, or never read). Screens show nothing.
 *   Set       the ticket numbers with a new reply.
 *
 * A walkthrough build never asks: its tickets are the browser demo, and its bell keeps
 * the mockup's number. Opening a real ticket marks it seen (markSeen), which removes the
 * mark at once and tells the server; the server's answer is then read again.
 *
 * Each read is a warehouse query, so a read is skipped when the last attempt is under
 * MIN_GAP_MS old (the app's warehouse cost) - a failed attempt counts too, so a
 * struggling server is not asked on every focus. markSeen reads again at once only
 * when the ticket had a mark: otherwise the server's answer cannot change.
 */

import { guardedFetch, isWalkthroughBuild } from './liveWrites.js';

export const MIN_GAP_MS = 30_000;

let numbers = null;
let lastAttemptAt = null;
let inFlight = null;
/* Bumped by every markSeen, before and after it tells the server. An answer to a read
 * that started before the server recorded the seen may still list the ticket just
 * opened, so that answer is dropped and a fresh read follows. */
let generation = 0;
/* Tickets whose seen is still on its way. An answer that arrives meanwhile may list
 * them; they stay unmarked until the server has the seen. */
const pendingSeen = new Map();
/* Set when an answer was dropped for a markSeen: that markSeen then reads again even
 * inside the gap, or a reply on another ticket would wait for the next screen. */
let dropped = false;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribeUnread(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const unreadNumbers = () => numbers;

/** Read the list again. A failed read keeps the last good answer. */
export function refreshUnread({ force = false, walkthrough = isWalkthroughBuild(), now = Date.now() } = {}) {
  if (walkthrough) return Promise.resolve(null);
  if (inFlight) return inFlight;
  if (!force && lastAttemptAt !== null && now - lastAttemptAt < MIN_GAP_MS) return Promise.resolve(numbers);
  lastAttemptAt = now;
  const started = generation;
  inFlight = (async () => {
    try {
      const res = await guardedFetch('/api/tickets/unread');
      if (!res.ok) return numbers;
      const body = await res.json();
      if (!body || !Array.isArray(body.unread)) return numbers;
      if (started !== generation) {
        dropped = true;
        return numbers;
      }
      numbers = new Set(body.unread.filter((n) => typeof n === 'string' && !pendingSeen.has(n)));
      emit();
      return numbers;
    } catch {
      return numbers;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** This person has now read this ticket's thread. Best effort: a failure changes nothing
 * the person did, and the next read puts the mark back if the server did not record it. */
export async function markSeen(number, { walkthrough = isWalkthroughBuild() } = {}) {
  if (walkthrough) return;
  generation += 1;
  const wasMarked = Boolean(numbers?.has(number));
  pendingSeen.set(number, (pendingSeen.get(number) || 0) + 1);
  if (wasMarked) {
    numbers = new Set([...numbers].filter((n) => n !== number));
    emit();
  }
  try {
    await guardedFetch(`/api/tickets/${encodeURIComponent(number)}/seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-requested-by': 'ihs-it-help' },
    });
  } catch {
    /* the next read decides */
  } finally {
    const left = pendingSeen.get(number) - 1;
    if (left > 0) pendingSeen.set(number, left);
    else pendingSeen.delete(number);
  }
  generation += 1;
  if (inFlight) await inFlight.catch(() => {});
  const force = wasMarked || numbers === null || dropped;
  dropped = false;
  await refreshUnread({ force });
}

/** For tests only. */
export function resetUnread() {
  numbers = null;
  lastAttemptAt = null;
  inFlight = null;
  generation = 0;
  dropped = false;
  pendingSeen.clear();
}
